import Vue from 'vue'
import Router from 'vue-router'
import FLogger from '@/utils/log/flogger'
import { locationDomain, operateLog } from '@/utils/tool'
import { useTokenCheck, clearUser, useLoginStatus, checkDpsPopupStatus } from '@/utils/useStore'
import { auto_sync } from '@/services/user'
import CONFIG from '@/config'
import routes from './routes'
import queryString from './core/query-string'
import store from './store'
import i18n, { setGlobalLanguage } from './i18n'

Vue.use(Router)
const originalPush = Router.prototype.push
let firstStartAppTime = 0 // 第一次启动APP时间，用来控制autoSync请求频率
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err)
}
let firstEntryCCB = true // 用户是否是在有token的情况下第一次进入ccb，新拉取token_check
const router = new Router({
  mode: 'history',
  base: import.meta.env.VITE_BASE_PATH,
  routes,
  linkActiveClass: 'active',
  linkExactActiveClass: 'active',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  }
})

/* 前端日志上报 */
window.fLog = new FLogger({
  url: CONFIG.FLOGGER_URL,
  appName: 'camcard-bussiness-pc',
  env: CONFIG.env,
  uid: store.state.user.userId,
  autoLogAjax: true,
  performKeys: [
    'rs',
    're',
    'fs',
    'dls',
    'dle',
    'cs',
    'rcs',
    'ce',
    'reqs',
    'ress',
    'ues',
    'uee',
    'dl',
    'rese',
    'di',
    'dcles',
    'dclee',
    'dc',
    'les',
    'lee'
  ],
  errnoNameArr: ['errno', 'err_no', 'error_no', 'err', 'errcode', 'ret'] // 选填 业务错误码可能出现的范围
})
Vue.config.errorHandler = function (err) {
  window.fLog.errorCallback(err)
  throw new Error(err)
}
// 统一跳转域名到b12013.camcard.com，b.camcard.com
locationDomain()
// 设置多语言
setGlobalLanguage(store)
/** ************设置路由**************** */
router.beforeEach((to, from, next) => {
  // 新用户注册登录，未完善公司信息时，切换了注册语言，登录成功后需要强制让他完善公司信息
  if (localStorage.resetLang) {
    setGlobalLanguage(store, localStorage.resetLang)
  }
  document.title = to.meta.title ? i18n.t(to.meta.title) : i18n.t('cc_seo_title')
  // let selfRouterList = ['/user/login', '/autoSave', '/colleague/view', '/edm/list', '/edm/draft', '/edm/edit'];

  // 登录页（不带任何查询参数）
  if (to.path == '/user/login' && JSON.stringify(to.query) == '{}') {
    // 防止IP限制页面进入死循环
    if (useLoginStatus()) {
      useTokenCheck({
        detail: 0,
        is_simpe: 1,
        success: () => {
          // token有效
          // 兼容名片夹、工具箱、管理模块国内先上，国外不上的情况：
          const APIList = store.state.user?.user_info?.APIList
          let api = APIList?.SiteAPI && APIList.SiteAPI[0]
          if (!api) {
            const user = JSON.parse(localStorage.ccb).user || {}
            api = user?.user_info?.APIList?.SiteAPI && user.user_info.APIList.SiteAPI[0]
          }
          if (__DEV__ || __TEST__) {
            next({ path: '/card/view', query: { first: 1 } })
          } else if (
            api === 'https://b104.camcard.com' &&
            location.hostname !== 'b104.camcard.com'
          ) {
            operateLog('afterEach-normal', 0.1)
            next()
          } else {
            if (api) {
              next({ path: '/card/view', query: { first: 1 } })
            } else {
              // token已失效，将store中的数据删除
              clearUser()
              next()
            }
          }
        },
        fail: () => next(),
        catchFn: e => {
          if (e.errno == 101 || e.errno == 105) {
            clearUser()
          }
          next()
        }
      })
    } else {
      next()
    }
  } else {
    /**
     * 从某些场景跳转过来，携带了token，并且首次访问ccb，此时重新token_check
     * 1、本地没有privilege_info，会导致跳转login页，例如新注册用户通过邮件激活链接点击进入ccb
     * 2、存在上一个用户的localStorage，会导致页面信息展示错误
     * 3、正常逻辑，如刷新页面，获取一下最新的信息
     */
    if (to.meta?.auth && useLoginStatus() && firstEntryCCB) {
      useTokenCheck({
        detail: 3,
        is_simpe: 0,
        success: () => {
          next()
        },
        fail: () => backToLogin(next, to),
        catchFn: () => backToLogin(next, to)
      })
    } else {
      next()
    }
  }
})
router.afterEach(to => {
  // 2020-11-24 add：由于现在nginx配置了模糊匹配，因此一些无效的，例如/edm/开头的错误路由将会来回无限跳转，因此这类路由因直接由else来处理，最终显示新的404页面（TODO）
  let isSelfRouterPrefix = false
  for (let i = 0; i < CONFIG.selfRouterPrefix.length; i++) {
    const item = CONFIG.selfRouterPrefix[i]
    if (to.path.startsWith(item)) {
      isSelfRouterPrefix = true
      break
    }
  }
  // 原系统页面，需要整理参数并用location跳转
  if (!CONFIG.selfRouterList.includes(to.path) && !isSelfRouterPrefix && !__DEV__) {
    const query = to.query
    const paramStr = queryString.stringify(query)
    const url = `${CONFIG.BASE_URL}${to.path}${to.hash}${paramStr.length > 0 ? '?' : ''}${paramStr}`
    location.href = url
  } else {
    // vue页面，直接跳转
    const preName = location.pathname.split('/')[1]
    if (useLoginStatus() && (preName === 'card' || preName === 'admin')) {
      checkDpsPopupStatus()
    }
    const autoSync = () => {
      // 2分钟内仅请求一次，不然频率太高
      const diff = (Date.now() - firstStartAppTime) / (60 * 1000)
      if (diff < 2) {
        console.log('diff:', diff)
        return
      }
      firstStartAppTime = Date.now()
      const link = location.href
      const list = ['/mobile/', 'wait', '/tool/export', '/tool/import', '/payment/', '/user/']
      const isExisted = list.every(i => !link.includes(i))
      if (isExisted) {
        try {
          auto_sync()
        } catch (error) {
          console.log('auto error')
        }
      }
    }
    // vue页面，直接跳转
    if (to.meta && to.meta.auth) {
      if (useLoginStatus()) {
        if (firstEntryCCB) {
          firstEntryCCB = false
          autoSync()
          return
        }
        useTokenCheck({
          detail: 3,
          is_simpe: 0,
          success: () => {
            autoSync()
          },
          fail: operateLog('backToLogin', 1),
          catchFn: e => {
            console.error('校验token失败', e)
            // 101: token没传或token长度不是24位
            // 105: token已失效
            if (e.errno == 101 || e.errno == 105) {
              clearUser()
              operateLog('backToLogin', 2)
              return backToLogin(null, to)
            }
          }
        })
      } else {
        operateLog('backToLogin', 3)
        return backToLogin(null, to)
      }
    } else {
      if (!useLoginStatus()) {
        if (to.path.includes('/user/') || !to?.meta?.auth) {
          // user 相关页面不做操作
          return operateLog('afterEach-login', 3)
        } else {
          backToLogin(null, to)
          return operateLog('backToLogin', 4)
        }
      }
      // 在白名单中
      // 兼容名片夹、工具箱、管理模块国内先上，国外不上的情况：
      const preName = location.pathname.split('/')[1]
      if (preName !== 'card' && preName !== 'tool' && preName !== 'admin' && preName !== 'crm') {
        return operateLog('afterEach-normal', 4)
      } else {
        operateLog('afterEach-normal', 5)
      }
    }
  }
})
function backToLogin(next, to) {
  let redirect = `${to.path}`
  if (Object.keys(to.query).length > 0) {
    redirect += `?${queryString.stringify(to.query)}`
  }
  if (next) {
    next({ path: '/user/login', query: { redirect }, replace: true })
  } else {
    location.replace(`/user/login?redirect=${redirect}`)
  }
}

export default router
